/**
 * Copyright (C) 2022 Viasat, Inc.
 * All rights reserved.
 * The information in this software is subject to change without notice and should not be construed as a commitment by Viasat, Inc.
 *
 * Viasat Proprietary
 * The Proprietary Information provided herein is proprietary to Viasat and
 * must be protected from further distribution and use. Disclosure to others,
 * use or copying without express written authorization of Viasat, is strictly prohibited.
 */
import { isNil } from 'lodash';
import React from 'react';
import { shallowEqual, useSelector } from 'react-redux';
import {
  selectIsInitialTerminalsRequestFinished,
  selectMapDimensions,
  selectMapPosition,
  selectMapSettingsFtvMapHeight,
  selectMapSettingsLabelsTileState,
  selectMapSettingsSatViewTileState,
  selectPrintMode,
  selectRouterPathname,
  selectSelectedOverlaidFlightLegId,
  selectPlatformListDrawerOpen,
  selectTerminalsInYourBeamPanelShown,
} from '../../store';
import { FLEET_MAP_ID, FTV_MAP_ID } from '../../store/maps/actions';
import { isPathnameFullTailView } from '../../utils/NavigatorUtils';
import BaseMapContainer from '../common/map/BaseMapContainer';
import { GOOGLE_MAP_INSTANCE_KEY } from '../common/map/GoogleMapSingleton';
import TerminalsInBeamMapControl from '../common/map_controls/TerminalsInBeamMapControl';
import { ToggleTileStateEnum } from '../materialui_wrappers/ToggleTile';
import { KU_PLUS_KA_DRAWER_WIDTH_PX } from './FleetMap';
import { TAIL_DETAILS_PANEL_WIDTH } from './TailDetailsPanel/TailDetailsPanel';

interface MapContainerProps {
  children: React.ReactNode;
}

/**
 * Global map container.
 */
const MapContainer = ({ children }: MapContainerProps) => {
  const mapSettingsFtvMapHeight = useSelector(selectMapSettingsFtvMapHeight(FTV_MAP_ID), shallowEqual);
  const mapSettingsLabelsTileState = useSelector(selectMapSettingsLabelsTileState(FLEET_MAP_ID), shallowEqual);
  const mapSettingsSatViewTileState = useSelector(selectMapSettingsSatViewTileState(FLEET_MAP_ID), shallowEqual);
  const printMode = useSelector(selectPrintMode, shallowEqual);
  const platformListDrawerOpen = useSelector(selectPlatformListDrawerOpen, shallowEqual);
  const isInitialTerminalsRequestFinished = useSelector(selectIsInitialTerminalsRequestFinished, shallowEqual);
  const routerPathname = useSelector(selectRouterPathname, shallowEqual);
  const terminalsInYourBeamPanelShown = useSelector(selectTerminalsInYourBeamPanelShown, shallowEqual);
  const selectedOverlaidFlightLegId = useSelector(selectSelectedOverlaidFlightLegId(FLEET_MAP_ID));

  // TODO: update these map ids when the maps split into multiple components
  const mapDimensions = useSelector(selectMapDimensions(FLEET_MAP_ID), shallowEqual);
  const mapPosition = useSelector(selectMapPosition(FLEET_MAP_ID), shallowEqual);
  const pathname = routerPathname.substr(1, routerPathname.length);
  const isFullTailView = isPathnameFullTailView(pathname);

  const isFullWidth = printMode || !isInitialTerminalsRequestFinished;

  // determine width
  let leftMargin = '32px';
  if (platformListDrawerOpen && !isFullTailView) {
    leftMargin = `${KU_PLUS_KA_DRAWER_WIDTH_PX}px`;
  } else if (isFullTailView) {
    leftMargin = `${TAIL_DETAILS_PANEL_WIDTH}px`;
  }

  const terminalInBeamPanelWidth = `${KU_PLUS_KA_DRAWER_WIDTH_PX}px`;

  const terminalInBeamPanelMargin = terminalsInYourBeamPanelShown ? terminalInBeamPanelWidth : '0px';
  let mapWidth = isFullWidth ? '100%' : `calc(100% - ${leftMargin} - ${terminalInBeamPanelMargin})`;

  // change width if overriden in Redux store
  mapWidth = mapDimensions?.width ? mapDimensions.width : mapWidth;

  // determine height
  let mapHeight = isFullTailView ? `${mapSettingsFtvMapHeight}px` : '100%';

  // change height if overriden in Redux store
  mapHeight = mapDimensions?.height ? mapDimensions.height : mapHeight;

  // determine left px offset
  let mapLeft = isFullWidth || mapDimensions ? '0px' : leftMargin;
  mapLeft = mapPosition?.left ? mapPosition.left + 'px' : mapLeft;

  // determine top px offset
  const top = mapPosition?.top ? mapPosition.top + 'px' : undefined;
  return (
    <BaseMapContainer
      id={GOOGLE_MAP_INSTANCE_KEY}
      width={mapWidth}
      height={mapHeight}
      mapLabelsEnabled={mapSettingsLabelsTileState === ToggleTileStateEnum.ON}
      mapSatelliteViewEnabled={mapSettingsSatViewTileState === ToggleTileStateEnum.ON}
      mapPerformanceOutlookLegendPanelEnabled={!isNil(selectedOverlaidFlightLegId)}
      left={mapLeft}
      top={top}
      additionalMapControls={(map: google.maps.Map, fullscreen: boolean) => (
        <TerminalsInBeamMapControl map={map} isFullScreen={fullscreen} isFullTailView={isFullTailView} />
      )}
      persistZoom
    >
      {children}
    </BaseMapContainer>
  );
};

export default MapContainer;
